import React, { useState, useEffect } from "react";
import SEO from "../seo";
const CheckoutSettings = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "contact-method",
        "customer-information",
        "cod-form-configuration",
        "abandoned-checkout",
        "tipping-configuration",
        "save-changes",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Checkout Settings | xPage Help Center"
        description="Learn how to configure Checkout Settings in xPage. Customize contact methods, customer information fields, COD form options, abandoned checkout recovery, tipping settings, and save changes for a seamless buying experience."
        keywords="xPage checkout settings, contact method, customer information, COD form, abandoned checkout recovery, tipping configuration, checkout customization, eCommerce checkout settings"
        canonical="https://help.xpage.ai/help/settings/Checkout%20Settings"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Checkout Settings
          </h1>
          <p className="text-sm text-systemLabel">
            The Checkout Settings section in Xpage is your command center for
            designing a seamless, efficient, and professional checkout
            experience. With Xpage, you don't need to start from
            scratch—pre-configured options and intelligent defaults allow you to
            configure your checkout quickly while maintaining full control and
            flexibility.
          </p>
        </header>

        {/* Contact Method Section */}
        <section id="contact-method" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Contact Method
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Xpage provides ready-to-use options for collecting customer
                contact information, making setup effortless:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Phone or Email
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      (recommended for flexibility)
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Email Only
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm"></td>
                  </tr>
                </tbody>
              </table>

              <p className="text-sm text-systemLabel">
                These choices let you adapt your checkout to different customer
                preferences and regional standards without manual configuration.
              </p>
            </div>
          </div>
        </section>

        {/* Customer Information Section */}
        <section id="customer-information" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Customer Information
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Collect only the information that matters to your business while
                keeping the process frictionless for customers. Xpage provides
                pre-set options that are easy to activate:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Full Name
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Require both first and last name to ensure accurate order
                      fulfillment.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Company Name
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Choose whether to include as Optional, Required, or Don't
                      Include.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Address Line 2
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Optional for apartments, units, or secondary address
                      details.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Shipping Address Phone Number
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Can be Required, Optional, or excluded to fit your
                      shipping policies.
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="text-sm text-systemLabel">
                Xpage's pre-configured choices simplify setup and let you
                quickly optimize the checkout form for your audience.
              </p>
            </div>
          </div>
        </section>

        {/* COD Form Configuration Section */}
        <section id="cod-form-configuration" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            COD Form Configuration
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                When enabling Cash on Delivery, Xpage offers ready-made form
                templates with required and optional fields to ensure orders are
                processed accurately:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Required Fields
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Full Name, Phone Number
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Optional Fields
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Country (required for international shipping), City,
                      Street Address, ZIP Code (optional for specific regions)
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="text-sm text-systemLabel">
                Access COD form settings directly via the Go To Payment Methods
                button, making customization effortless.
              </p>
            </div>
          </div>
        </section>

        {/* Abandoned Checkout Section */}
        <section id="abandoned-checkout" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Abandoned Checkout
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Xpage helps recover lost sales by providing pre-set reminder
                intervals for abandoned carts:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        10 minutes
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      (Recommended)
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        1 Hour
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm"></td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        6 Hours
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm"></td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        24 Hours
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm"></td>
                  </tr>
                </tbody>
              </table>

              <p className="text-sm text-systemLabel">
                These flexible defaults allow you to select the optimal timing
                for nudging customers to complete their purchase without manual
                scheduling.
              </p>
            </div>
          </div>
        </section>

        {/* Tipping Configuration Section */}
        <section id="tipping-configuration" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Tipping Configuration
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Enable Tipping at checkout to provide customers with the option
                to leave gratuities.
              </p>

              <p className="text-sm text-systemLabel">
                Xpage lets you easily toggle this feature on or off and
                customize the description to align with your brand tone.
              </p>
            </div>
          </div>
        </section>

        {/* Save Changes Section */}
        <section id="save-changes" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Save Changes
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Apply all changes instantly using the Save button at the bottom
                of the page. Xpage ensures that your settings are applied across
                all checkout experiences without additional steps.
              </p>
            </div>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                By leveraging Xpage's pre-configured choices and flexible
                checkout options, you can deliver a professional, efficient, and
                customer-friendly checkout experience with minimal effort.
                Streamlined setup, intelligent defaults, and full customization
                allow you to maximize conversions, reduce friction, and maintain
                a polished brand experience.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#contact-method"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "contact-method"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Contact Method
              </a>
            </li>
            <li>
              <a
                href="#customer-information"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "customer-information"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Customer Information
              </a>
            </li>
            <li>
              <a
                href="#cod-form-configuration"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "cod-form-configuration"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                COD Form Configuration
              </a>
            </li>
            <li>
              <a
                href="#abandoned-checkout"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "abandoned-checkout"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Abandoned Checkout
              </a>
            </li>
            <li>
              <a
                href="#tipping-configuration"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "tipping-configuration"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Tipping Configuration
              </a>
            </li>
            <li>
              <a
                href="#save-changes"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "save-changes"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Save Changes
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSettings;
